import type { ConnectorStatusView } from "../api";

export interface OwnerConnectorStatusCopy {
  family: string;
  capability: string;
  statusLabel: string;
  message: string;
  action: string;
  primaryCta: string;
  safeCta: string;
  detailsCta: string;
  ownerPath: string;
  risk: string | null;
  evidence: string;
}

export function connectorFamily(id: string): string {
  if (id === "gmail" || id === "gcal") return "Google";
  if (id === "outlook" || id === "outlook-cal" || id === "teams") return "Microsoft";
  if (id === "github") return "GitHub";
  if (id === "itsimo") return "IT-Simo";
  return "Other";
}

function connectorDisplayName(connector: Pick<ConnectorStatusView, "id" | "name">): string {
  if (connector.id === "gmail") return "Gmail";
  if (connector.id === "gcal") return "Google Kalender";
  return connector.name;
}

function hasTechnicalConnectorDetail(value: string | undefined): boolean {
  return Boolean(
    value &&
      /invalid_grant|token endpoint|expired or revoked|bearer\s+[a-z0-9._-]{8,}|authorization:\s*bearer|secret|api[_-]?key|fetch failed|networkerror|econnreset|enotfound|etimedout|timeout|stack trace|traceback|exception/i.test(value),
  );
}

export function connectorCapability(id: string): string {
  const capabilities: Record<string, string> = {
    gmail: "Gmail lesen und mailbezogene Missionen vorbereiten",
    gcal: "Google Kalender pruefen und Termin-Kontext liefern",
    outlook: "Outlook Mail lesen und Entwuerfe vorbereiten",
    "outlook-cal": "Outlook Kalender pruefen und Termin-Kontext liefern",
    teams: "Teams-Kontext lesen und Nachrichten gated vorbereiten",
    github: "GitHub PRs, Issues und Notifications lesen",
    itsimo: "IT-Simo Tickets und interne API-Kontexte lesen",
  };
  return capabilities[id] ?? "Connector-Live-Daten fuer Missionen bereitstellen";
}

export function ownerConnectorStatusCopy(connector: ConnectorStatusView): OwnerConnectorStatusCopy {
  const family = connectorFamily(connector.id);
  const displayName = connectorDisplayName(connector);
  const capability = connectorCapability(connector.id);
  if (connector.reachable) {
    const technicalDetail = hasTechnicalConnectorDetail(connector.message);
    return {
      family,
      capability,
      statusLabel: "verbunden",
      message: technicalDetail
        ? `${displayName} ist verbunden; technische Statusdetails bleiben als serverseitige Evidence verborgen.`
        : connector.message || `${displayName} ist verbunden.`,
      action: "Keine Aktion noetig",
      primaryCta: "Live-Daten nutzen",
      safeCta: "Ohne Aenderung fortfahren",
      detailsCta: "Connector-Details anzeigen",
      ownerPath: "kein Settings-Schritt erforderlich",
      risk: null,
      evidence: technicalDetail
        ? "Connector meldet erreichbar; technische Statusdetails werden nicht in der Owner-UI angezeigt."
        : "Letzter erfolgreicher Live-Read wird vom Connector-Status noch nicht gemeldet.",
    };
  }
  if (connector.issueCode === "reauth_required" || connector.state === "needs_reauth" || /invalid_grant|expired|revoked|neu verbinden|reauth/i.test(connector.message ?? "")) {
    const action = connector.actionLabel ?? (connector.action === "reauth" ? "Neu verbinden" : "Neu verbinden");
    return {
      family,
      capability,
      statusLabel: action,
      message: `Reauth erforderlich: ${displayName} getrennt: Google-/OAuth-Freigabe ist abgelaufen oder widerrufen.`,
      action,
      primaryCta: action,
      safeCta: `Ohne ${displayName}-Daten fortfahren`,
      detailsCta: "Reauth-Details anzeigen",
      ownerPath: `Settings > Connectoren > ${action}`,
      risk: connector.risk ?? `${displayName}-Live-Daten stehen bis zur erneuten Freigabe nicht zur Verfuegung.`,
      evidence: "Kein belastbarer letzter Erfolg im UI-Status; Reauth vor Live-Daten-Nutzung erforderlich.",
    };
  }
  if (!connector.configured || connector.issueCode === "credentials_missing" || connector.issueCode === "authorization_missing" || connector.state === "missing_credentials" || connector.state === "not_authorized") {
    const action = connector.actionLabel ?? (connector.action === "connect" ? "Neu verbinden" : "Secret oder OAuth in den Einstellungen hinterlegen");
    return {
      family,
      capability,
      statusLabel: connector.action === "connect" ? "Reauth erforderlich" : connector.actionLabel ?? "Setup erforderlich",
      message: `${displayName} ist noch nicht vollstaendig eingerichtet.`,
      action,
      primaryCta: action,
      safeCta: `Ohne ${displayName}-Daten fortfahren`,
      detailsCta: "Setup-Details anzeigen",
      ownerPath: `Settings > Connectoren > ${action}`,
      risk: connector.risk ?? `${displayName}-Live-Daten sind nicht verfuegbar.`,
      evidence: "Kein letzter Erfolg gemeldet; Setup-Evidence fehlt.",
    };
  }
  const action = connector.actionLabel ?? "Setup pruefen";
  const technicalDetail = hasTechnicalConnectorDetail(connector.message);
  return {
    family,
    capability,
    statusLabel: action,
    message: technicalDetail
      ? `${displayName} ist aktuell nicht belastbar erreichbar. Technische Details bleiben als serverseitige Evidence verborgen.`
      : connector.message || `${displayName} ist aktuell nicht erreichbar.`,
    action,
    primaryCta: action,
    safeCta: `Ohne ${displayName}-Daten fortfahren`,
    detailsCta: "Connector-Details anzeigen",
    ownerPath: `Settings > Connectoren > ${action}`,
    risk: connector.risk ?? `${displayName}-Live-Daten koennen unvollstaendig sein.`,
    evidence: "Kein letzter Erfolg gemeldet; aktuelle Meldung ist nur Status-Evidence.",
  };
}
